import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckIcon } from '@heroicons/react/24/solid';
import '../styles/Home.css';
import { redirectToCheckout } from '../stripe/config';

const Pricing = () => {
  const [isAnnual] = useState(true);
  const blueprintContainerRef = useRef(null);
  useEffect(() => {
    // Create blueprint elements
    const createBlueprintElements = () => {
      if (!blueprintContainerRef.current) return;

      // Add measurement lines
      const measurementTypes = [
        { className: 'vertical-measure-1 measurement', length: '150px' },
        { className: 'vertical-measure-2 measurement', length: '200px' },
        { className: 'horizontal-measure-1 measurement', length: '180px' },
        { className: 'horizontal-measure-2 measurement', length: '250px' }
      ];

      measurementTypes.forEach((measure) => {
        const line = document.createElement('div');
        line.className = measure.className;
        line.style.height = measure.length;
        blueprintContainerRef.current.appendChild(line);
      });

      // Add construction elements
      const toolTypes = [
        { className: 'ruler construction-element', position: { top: '15%', left: '10%' } },
        { className: 'compass construction-element', position: { top: '25%', right: '15%' } },
        { className: 'hammer construction-element', position: { bottom: '20%', left: '20%' } }
      ];

      toolTypes.forEach((tool) => {
        const toolElement = document.createElement('div');
        toolElement.className = tool.className;
        toolElement.style.top = tool.position.top;
        toolElement.style.left = tool.position.left;
        toolElement.style.right = tool.position.right;
        toolElement.style.bottom = tool.position.bottom;
        blueprintContainerRef.current.appendChild(toolElement);
      });
    };

    createBlueprintElements();

    return () => {
      if (blueprintContainerRef.current) {
        blueprintContainerRef.current.innerHTML = '';
      }
    };
  }, []);

  const plans = [
    {
      name: 'Professional',
      price: 223,
      description: 'Complete solution for decking businesses',
      features: [
        'Unlimited project management',
        'Advanced material calculations',
        'Scheduling tools',
        'Email & Phone support',
        'Multi-crew scheduling',
        'Advanced analytics and reporting'
      ],
      highlighted: true,
      cta: 'Start Now'
    },
    {
      name: 'Enterprise',
      price: null,
      description: 'Advanced features for larger operations',
      features: [
        'Everything in Professional',
        '3D design tools',
        'Custom integrations',
        'Dedicated account manager',
        '24/7 premium support',
        'Advanced customization options',
      ],
      highlighted: false,
      cta: 'Coming Soon'
    }
  ];

  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6 }
    }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  return (
    <div className="relative min-h-screen">
      {/* Background Elements */}
      <div ref={blueprintContainerRef} className="blueprint-container" />
      
      {/* Hero Section */}
      <section className="relative pt-24 pb-16">
        <div className="absolute inset-0 bg-gradient-to-br from-primary-900/95 to-secondary-900/95">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zNiAzNGMwIDIuMjA5LTEuNzkxIDQtNCA0cy00LTEuNzkxLTQtNCAxLjc5MS00IDQtNCA0IDEuNzkxIDQgNHoiIGZpbGw9IiM2NEE1RkYiIGZpbGwtb3BhY2l0eT0iLjEiLz48L2c+PC9zdmc+')] opacity-20" />
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center max-w-3xl mx-auto">
            <motion.div
              initial="hidden"
              animate="visible"
              variants={staggerContainer}
            >
              <motion.h1 
                className="text-4xl md:text-5xl font-bold text-white mb-6"
                variants={fadeIn}
              >
                Simple, Transparent Pricing
              </motion.h1>
              <motion.p 
                className="text-xl text-gray-200 mb-8"
                variants={fadeIn}
              >
                Choose the perfect plan for your decking business
              </motion.p>


            </motion.div>
          </div>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="relative py-16 -mt-8">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {plans.map((plan, index) => (
              <motion.div
                key={plan.name}
                className={`relative rounded-2xl shadow-xl overflow-hidden backdrop-blur-sm ${
                  plan.highlighted 
                    ? 'bg-white/90 ring-4 ring-primary-500 transform md:-translate-y-4'
                    : 'bg-white/80'
                }`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.2 }}
                whileHover={{ y: -5, transition: { duration: 0.2 } }}
              >
                {plan.highlighted && (
                  <div className="absolute top-0 right-0 -translate-y-1/2 translate-x-1/2 bg-primary-500 text-white text-sm px-4 py-1 rounded-full font-medium">
                    Most Popular
                  </div>
                )}
                <div className="p-8">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">{plan.name}</h3>
                  <p className="text-gray-600 mb-6">{plan.description}</p>
                  <div className="flex items-baseline mb-8">
                    {plan.price ? (
                      <>
                        <span className="text-4xl font-bold text-gray-900">${plan.price}</span>
                        <span className="text-gray-600 ml-2">/ month</span>
                      </>
                    ) : (
                      <span className="text-4xl font-bold text-primary-600">Coming Soon</span>
                    )}
                  </div>
                  <ul className="space-y-4 mb-8">
                    {plan.features.map((feature, i) => (
                      <li key={i} className="flex items-start">
                        <CheckIcon className="h-5 w-5 text-primary-500 mt-0.5 mr-3 flex-shrink-0" />
                        <span className="text-gray-600">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  {plan.price ? (
                    <button
                      onClick={async () => {
                        try {
                          console.log('Starting checkout with price ID:', process.env.REACT_APP_STRIPE_PROFESSIONAL_PRICE_ID);
                          await redirectToCheckout(process.env.REACT_APP_STRIPE_PROFESSIONAL_PRICE_ID);
                        } catch (error) {
                          console.error('Checkout error:', error);
                          alert('There was an error starting the checkout process. Please try again.');
                        }
                      }}
                      className={`block w-full text-center py-3 px-6 rounded-lg font-medium transition-colors ${
                        plan.highlighted
                          ? 'bg-primary-600 text-white hover:bg-primary-700'
                          : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
                      }`}
                    >
                      {plan.cta}
                    </button>
                  ) : (
                    <Link
                      to="/contact"
                      className="block w-full text-center py-3 px-6 rounded-lg font-medium transition-colors bg-gray-100 text-gray-900 hover:bg-gray-200"
                    >
                      {plan.cta}
                    </Link>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="relative py-16">
        <div className="absolute inset-0 bg-white/80 backdrop-blur-sm" />
        <div className="container mx-auto px-4 max-w-4xl relative z-10">
          <motion.h2 
            className="text-3xl font-bold text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            Frequently Asked Questions
          </motion.h2>
          <div className="space-y-8">
            {[
              {
                question: "What’s included when you sign up?",
                answer: "Complete access to Nail the Cost’s powerful tools — create accurate quotes, manage expenses, and maximize profit on every job."
              },
              {
                question: "Can I change my plan later?",
                answer: "At this time, Nail the Cost offers one complete plan that includes all features. Additional plan options will be available in the future."
              },
              {
                question: "What makes Nail the Cost different?",
                answer: "Unlike generic quoting tools, Nail the Cost was built specifically for deck builders. Every feature is designed to fit the way you actually work — fast, accurate, and job-ready."
              }
            ].map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <h3 className="text-xl font-semibold mb-2">{faq.question}</h3>
                <p className="text-gray-600">{faq.answer}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Pricing; 
